import { useState } from 'react';
import { SigmaIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { WorkbookDefinition } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/** Add a pivot table: choose the Source or Worksheet it summarizes. */
export function AddPivotPanel({
  definition,
  addPivot,
}: {
  definition: WorkbookDefinition;
  addPivot: (input: { id: string; name: string }) => void;
}) {
  const gt = useGT();
  const inputs = [...definition.sources, ...definition.sheets];
  const [inputId, setInputId] = useState(inputs[0]?.id ?? '');
  const input = inputs.find((item) => item.id === inputId);
  return (
    <div className="space-y-3">
      <h2 className="font-medium">
        <T context="workbook add pivot heading">Add a pivot table</T>
      </h2>
      <p className="text-sm text-muted-foreground">
        <T context="workbook add pivot guidance">
          A pivot table groups another table's rows and summarizes them: sums,
          counts, averages and shares of a total. Other sheets, reports and
          files read its result like any worksheet's.
        </T>
      </p>
      {inputs.length ? (
        <div className="flex items-center gap-2">
          <Select value={inputId || null} onValueChange={setInputId}>
            <SelectTrigger
              className="min-w-56"
              aria-label={gt('Data to summarize', {
                context: 'workbook new pivot input selector',
              })}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {inputs.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!input}
            onClick={() => {
              if (input) addPivot(input);
            }}
          >
            <SigmaIcon />
            <T context="workbook create pivot">Create pivot table</T>
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          <T context="workbook no inputs for pivot">
            Add a source first using the plus button beside Data.
          </T>
        </p>
      )}
    </div>
  );
}
